import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listAppointments,
  updateAppointmentStatus,
  adminLogout,
  listAdminAbsences,
  createAbsence,
  removeAbsence,
  ApiError,
  type Appointment,
  type AppointmentStatus,
  type Absence,
} from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { formatAbsenceRangeFr } from '@/constants';

const STATUS_LABEL: Record<AppointmentStatus, string> = {
  pending: 'En attente',
  confirmed: 'Confirmé',
  cancelled: 'Annulé',
  completed: 'Terminé',
};

const STATUS_STYLE: Record<AppointmentStatus, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-stone-100 text-stone-500 border-stone-200',
  completed: 'bg-sky-50 text-sky-700 border-sky-200',
};

const FILTERS: Array<{ value: AppointmentStatus | 'all'; label: string }> = [
  { value: 'pending', label: 'En attente' },
  { value: 'confirmed', label: 'Confirmés' },
  { value: 'completed', label: 'Terminés' },
  { value: 'cancelled', label: 'Annulés' },
  { value: 'all', label: 'Tous' },
];

function formatDate(date: string) {
  try {
    return new Date(`${date}T00:00:00`).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return date;
  }
}

export default function Admin() {
  const navigate = useNavigate();
  const { authenticated, loading: checking } = useAuth();

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [filter, setFilter] = useState<AppointmentStatus | 'all'>('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  // Appointment awaiting confirmation in the modal (replaces window.confirm,
  // which is unreliable on iOS).
  const [confirmTarget, setConfirmTarget] = useState<Appointment | null>(null);

  const [absences, setAbsences] = useState<Absence[]>([]);
  const [absencesLoading, setAbsencesLoading] = useState(false);
  const [absenceBusy, setAbsenceBusy] = useState(false);
  const [absenceFrom, setAbsenceFrom] = useState('');
  const [absenceTo, setAbsenceTo] = useState('');
  const [absenceLabel, setAbsenceLabel] = useState('');

  useEffect(() => {
    document.title = 'Rendez-vous — LIFECUPP';

    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    robots.setAttribute('data-admin-seo', 'true');
    document.head.appendChild(robots);

    return () => {
      document.querySelectorAll('meta[data-admin-seo]').forEach((el) => el.remove());
    };
  }, []);

  useEffect(() => {
    if (!checking && !authenticated) navigate('/auth?redirect=/admin', { replace: true });
  }, [checking, authenticated, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setAppointments(await listAppointments(filter === 'all' ? undefined : filter));
    } catch (err) {
      // The session can expire while the page is open.
      if (err instanceof ApiError && err.status === 401) {
        navigate('/auth?redirect=/admin', { replace: true });
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => {
    if (authenticated) void load();
  }, [authenticated, load]);

  const loadAbsences = useCallback(async () => {
    setAbsencesLoading(true);
    try {
      setAbsences(await listAdminAbsences());
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        navigate('/auth?redirect=/admin', { replace: true });
        return;
      }
      // Don't wipe the whole page with a top error — appointments still load.
      console.error('absences:', err instanceof ApiError ? err.message : err);
      setAbsences([]);
    } finally {
      setAbsencesLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (authenticated) void loadAbsences();
  }, [authenticated, loadAbsences]);

  const addAbsence = async (e: React.FormEvent) => {
    e.preventDefault();
    setAbsenceBusy(true);
    setError('');
    setNotice('');
    try {
      await createAbsence({
        from: absenceFrom,
        to: absenceTo || absenceFrom,
        label: absenceLabel.trim() || undefined,
      });
      setAbsenceFrom('');
      setAbsenceTo('');
      setAbsenceLabel('');
      setNotice('Période d’indisponibilité ajoutée');
      await loadAbsences();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        navigate('/auth?redirect=/admin', { replace: true });
        return;
      }
      setError(
        err instanceof ApiError
          ? err.errors?.join(' ') || err.message
          : 'Impossible d’ajouter l’absence',
      );
    } finally {
      setAbsenceBusy(false);
    }
  };

  const deleteAbsenceRow = async (id: number) => {
    setAbsenceBusy(true);
    setError('');
    setNotice('');
    try {
      await removeAbsence(id);
      setNotice('Période supprimée');
      await loadAbsences();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        navigate('/auth?redirect=/admin', { replace: true });
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Suppression impossible');
    } finally {
      setAbsenceBusy(false);
    }
  };

  const changeStatus = async (appointment: Appointment, status: AppointmentStatus) => {
    setBusyId(appointment.id);
    setError('');
    setNotice('');

    try {
      const result = await updateAppointmentStatus(appointment.id, status);

      // The server confirms the appointment even if the mail fails, and says so.
      if (result.warning) {
        setError(result.warning);
      } else if (result.emailed) {
        setNotice(`E-mail de confirmation envoyé à ${appointment.email}`);
      } else {
        setNotice('Statut mis à jour');
      }

      await load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        navigate('/auth?redirect=/admin', { replace: true });
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Mise à jour impossible');
    } finally {
      setBusyId(null);
    }
  };

  // Runs the confirmation once the admin accepts the modal. Kept open (with a
  // sending state) until the request finishes, then closed.
  const confirmAppointment = async () => {
    if (!confirmTarget) return;
    await changeStatus(confirmTarget, 'confirmed');
    setConfirmTarget(null);
  };

  // Escape closes the modal, but not mid-send.
  useEffect(() => {
    if (!confirmTarget) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && busyId === null) setConfirmTarget(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmTarget, busyId]);

  const handleLogout = async () => {
    await adminLogout().catch(() => undefined);
    navigate('/auth', { replace: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <p className="text-sm text-stone-500">Chargement…</p>
      </div>
    );
  }

  if (!authenticated) return null;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-bold text-stone-800">Rendez-vous</h1>
            <p className="text-xs text-stone-500">LIFECUPP by bien-être</p>
          </div>
          <button
            onClick={handleLogout}
            className="text-sm text-stone-500 hover:text-stone-800 transition-colors"
          >
            Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {notice && (
          <div className="mb-4 p-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-100">
            {notice}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100">
            {error}
          </div>
        )}

        <section className="mt-2" aria-labelledby="rdv-list-heading">
          <div className="mb-4">
            <h2 id="rdv-list-heading" className="font-semibold text-stone-800 text-base">
              Demandes de rendez-vous
            </h2>
            <p className="text-xs text-stone-500 mt-0.5">
              Filtrez par statut. Par défaut : en attente de confirmation.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFilter(option.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                  filter === option.value
                    ? 'bg-emerald-700 text-white border-emerald-700'
                    : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-sm text-stone-500 py-12 text-center">Chargement…</p>
          ) : appointments.length === 0 ? (
            <p className="text-sm text-stone-500 py-12 text-center">
              Aucun rendez-vous dans cette catégorie.
              {filter === 'pending' && (
                <span className="block mt-1 text-stone-400">
                  Essayez « Tous » ou « Confirmés » si vous cherchez d’anciennes demandes.
                </span>
              )}
            </p>
          ) : (
          <div className="space-y-3">
            {appointments.map((appointment) => (
              <article
                key={appointment.id}
                className="bg-white rounded-xl border border-stone-200 p-4 sm:p-5"
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <h2 className="font-semibold text-stone-800">
                      {appointment.prenom} {appointment.nom}
                    </h2>
                    <p className="text-sm text-stone-500">{appointment.service}</p>
                  </div>
                  <span
                    className={`text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS_STYLE[appointment.status]}`}
                  >
                    {STATUS_LABEL[appointment.status]}
                  </span>
                </div>

                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm mb-3">
                  <div className="flex gap-2">
                    <dt className="text-stone-500">Date</dt>
                    <dd className="text-stone-800 font-medium">
                      {formatDate(appointment.date)} à {appointment.heure}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-stone-500">Téléphone</dt>
                    <dd>
                      <a href={`tel:${appointment.telephone}`} className="text-emerald-700 hover:underline">
                        {appointment.telephone}
                      </a>
                    </dd>
                  </div>
                  <div className="flex gap-2 sm:col-span-2">
                    <dt className="text-stone-500">E-mail</dt>
                    <dd>
                      <a href={`mailto:${appointment.email}`} className="text-emerald-700 hover:underline break-all">
                        {appointment.email}
                      </a>
                    </dd>
                  </div>
                </dl>

                {appointment.message && (
                  <p className="text-sm text-stone-600 bg-stone-50 rounded-lg p-3 mb-3 whitespace-pre-line">
                    {appointment.message}
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  {appointment.status !== 'confirmed' && (
                    <button
                      onClick={() => setConfirmTarget(appointment)}
                      disabled={busyId === appointment.id}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-700 hover:bg-emerald-800 text-white transition-colors disabled:opacity-50"
                    >
                      Confirmer
                    </button>
                  )}
                  {appointment.status === 'confirmed' && (
                    <button
                      onClick={() => changeStatus(appointment, 'completed')}
                      disabled={busyId === appointment.id}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium bg-sky-700 hover:bg-sky-800 text-white transition-colors disabled:opacity-50"
                    >
                      Marquer terminé
                    </button>
                  )}
                  {appointment.status !== 'cancelled' && (
                    <button
                      onClick={() => changeStatus(appointment, 'cancelled')}
                      disabled={busyId === appointment.id}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium bg-white border border-stone-200 text-stone-600 hover:border-stone-300 transition-colors disabled:opacity-50"
                    >
                      Annuler
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
          )}
        </section>

        {/* Absences — below RDVs (rare use) */}
        <section className="bg-white rounded-xl border border-stone-200 p-4 sm:p-5 mt-10">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="font-semibold text-stone-800">Indisponibilités</h2>
              <p className="text-xs text-stone-500 mt-0.5">
                Périodes bloquées sur le formulaire public (ex. congés). Supprimez-les à tout moment.
              </p>
            </div>
          </div>

          <form
            onSubmit={addAbsence}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4"
          >
            <div>
              <label className="block text-xs text-stone-500 mb-1" htmlFor="abs-from">
                Du
              </label>
              <input
                id="abs-from"
                type="date"
                required
                value={absenceFrom}
                onChange={(e) => setAbsenceFrom(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1" htmlFor="abs-to">
                Au
              </label>
              <input
                id="abs-to"
                type="date"
                required
                value={absenceTo}
                min={absenceFrom || undefined}
                onChange={(e) => setAbsenceTo(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1" htmlFor="abs-label">
                Libellé <span className="text-stone-400">(optionnel)</span>
              </label>
              <input
                id="abs-label"
                type="text"
                maxLength={120}
                placeholder="Congés, formation…"
                value={absenceLabel}
                onChange={(e) => setAbsenceLabel(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm"
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={absenceBusy}
                className="w-full px-3 py-2 rounded-lg text-sm font-medium bg-emerald-700 hover:bg-emerald-800 text-white transition-colors disabled:opacity-50"
              >
                {absenceBusy ? '…' : 'Ajouter'}
              </button>
            </div>
          </form>

          {absencesLoading ? (
            <p className="text-sm text-stone-500">Chargement…</p>
          ) : absences.length === 0 ? (
            <p className="text-sm text-stone-500">Aucune période d’indisponibilité.</p>
          ) : (
            <ul className="divide-y divide-stone-100 border border-stone-100 rounded-lg">
              {absences.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-sm"
                >
                  <div>
                    <span className="font-medium text-stone-800 capitalize">
                      {formatAbsenceRangeFr({ from: row.from, to: row.to })}
                    </span>
                    {row.label && (
                      <span className="text-stone-500 ml-2">— {row.label}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => deleteAbsenceRow(row.id)}
                    disabled={absenceBusy}
                    className="text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    Supprimer
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      {confirmTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
          onClick={() => busyId === null && setConfirmTarget(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-check-line text-emerald-700 text-xl"></i>
              </div>
              <div>
                <h3 id="confirm-title" className="font-semibold text-stone-800">
                  Confirmer ce rendez-vous ?
                </h3>
                <p className="text-sm text-stone-500 mt-0.5">
                  {confirmTarget.prenom} {confirmTarget.nom} — {formatDate(confirmTarget.date)} à {confirmTarget.heure}
                </p>
              </div>
            </div>

            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 mb-5 text-sm text-emerald-800 flex items-start gap-2">
              <i className="ri-mail-send-line mt-0.5 flex-shrink-0"></i>
              <span>
                Un e-mail de confirmation sera envoyé à{' '}
                <strong className="break-all">{confirmTarget.email}</strong>.
              </span>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmTarget(null)}
                disabled={busyId === confirmTarget.id}
                className="px-4 py-2 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmAppointment}
                disabled={busyId === confirmTarget.id}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-700 hover:bg-emerald-800 text-white transition-colors disabled:opacity-50"
              >
                {busyId === confirmTarget.id ? 'Envoi…' : 'Confirmer et envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
